/* eslint-disable react/no-multi-comp */
import { Box } from '@customink/pigment-react';
import {
  useDesktopBreakpoint,
  useMobileBreakpoint,
  useProduct,
  useWCIBMO,
} from '@customink/project-service-react-components';
import type { ProjectItem as ProjectItemType } from '@customink/project-service-react-components';
import type { ReactEventHandler } from 'react';
import { useState } from 'react';

import { useHasOutOfStockSizes } from './hooks';
import ProjectItemAddModal from './ProjectItemAddModal';
import { ProjectItemButtons } from './ProjectItemButtons';
import { ProjectItemDetails } from './ProjectItemDetails';
import { ProjectItemPrice } from './ProjectItemPrice';
import { ProjectItemProductName } from './ProjectItemProductName';
import { ProjectItemProof } from './ProjectItemProof';
import { ProjectItemQuantityWithPrice } from './ProjectItemQuantityWithPrice';
import { ProjectItemSizes } from './ProjectItemSizes';
import { googleAnalyticsTag } from '../vendors/googleAnalytics';

import type { ModalState } from '@/atoms/editSizesModalAtoms';
import CanadaToUS from '@/components/Alerts/CanadaToUs';
import UnavailableSizes from '@/components/Alerts/UnavailableSizes';
import UsToCanada from '@/components/Alerts/UsToCanada';
import ErrorBoundary from '@/components/ErrorBoundary';
import PersonalizationCountInfo from '@/components/ProjectItem/PersonalizationCountInfo';
import { GA_EVENTS } from '@/constants';
import { useDeleteItems } from '@/hooks/ProjectItem';
import { useShowStrikethroughPrice } from '@/hooks/useShowStrikethroughPrice';
import { spacing16, spacing4 } from '@/utils/designTokens';
import { pageViewTag } from '@/utils/metrics';

type ProjectItemProps = {
  item: ProjectItemType;
  isAnchorItem?: boolean;
  onEditSizes?: (modalState?: ModalState) => void;
};

export function ProjectItem({ item, onEditSizes }: ProjectItemProps) {
  const [deleteItems] = useDeleteItems();
  const { data: product } = useProduct({ colorId: item.product?.colorId });
  const hasOutOfStockSizes = useHasOutOfStockSizes(item);
  const showStrikethroughPrice = useShowStrikethroughPrice();
  const isMobileBreakpoint = useMobileBreakpoint();

  const { wcibmo } = useWCIBMO(item.design?.cid);

  const [isAddingColor, setIsAddingColor] = useState(false);

  if (!item.id) return null;

  const isCanadian = product?.variants?.some(
    (variant) => variant.region === 'Canada' && variant.style_id === product.id,
  );
  const hasCAWcibmo = isCanadian && wcibmo === 'canada_product_to_us';
  const hasUSWcibmo = !isCanadian && wcibmo === 'us_product_to_canada';

  const displayDraft = item.totalQuantity === 0;

  const handleEditItemClick: ReactEventHandler = () => {
    pageViewTag(`checkout/edit-sizes`);
    onEditSizes?.();
  };

  const handleAddItemClick = () => {
    pageViewTag(`checkout/addcolor`);
    setIsAddingColor(true);
  };

  const handleAddItemClose = () => {
    setIsAddingColor(false);
  };

  const handleRemoveItemClick = () => {
    if (!item?.id) return;
    pageViewTag(`checkout/summary/removed`);
    googleAnalyticsTag({
      event: GA_EVENTS.REMOVE_FROM_CART,
    });
    deleteItems([item.id]);
    // Scroll up after removing item (mobile only)
    if (isMobileBreakpoint) {
      window.scrollBy({
        top: -300,
        behavior: 'smooth',
      });
    }
  };

  return (
    <ErrorBoundary>
      <Box data-testid="project-item">
        {hasOutOfStockSizes && (
          <Box style={{ marginBottom: '16px' }}>
            <UnavailableSizes />
          </Box>
        )}
        {hasCAWcibmo ? <CanadaToUS /> : hasUSWcibmo ? <UsToCanada /> : null}
        <Box
          alignItems="start"
          className="pje-ProjectItemGrid"
          columnGap={spacing16}
          data-testid="project-item-grid"
          display="grid"
          gridTemplateColumns={
            isMobileBreakpoint ? '33% auto 1fr' : '211px auto 1fr'
          }
          gridTemplateRows="repeat(4, min-content) 1fr min-content"
          position="relative"
          rowGap={spacing4}
        >
          <Box
            data-testid="project-item-proof"
            gridColumn="1"
            gridRow={isMobileBreakpoint ? '1/-2' : '1/-1'}
          >
            <ProjectItemProof displayDraft={displayDraft} item={item} />
          </Box>
          <Box data-testid="project-item-product-name" gridRow="1">
            <ProjectItemProductName item={item} />
          </Box>
          <Box
            data-testid="project-item-price"
            gridColumn="3"
            gridRow="1/3"
            justifySelf="end"
            textAlign="right"
          >
            <ProjectItemPrice item={item} />
          </Box>
          <Box
            data-testid="project-item-details"
            gridColumn={showStrikethroughPrice ? '2' : '2/4'}
            gridRow="2"
          >
            <ProjectItemDetails item={item} />
          </Box>
          <Box
            data-testid="project-item-quantity-with-price"
            gridColumn="2/4"
            gridRow="3"
          >
            <ProjectItemQuantityWithPrice item={item} />
          </Box>
          <Box data-testid="project-item-sizes" gridColumn="2/4" gridRow="4">
            <ProjectItemSizes editSizes={onEditSizes} item={item} />
          </Box>
          <Box
            data-testid="project-item-buttons"
            gridColumn={isMobileBreakpoint ? '1/4' : '2/4'}
            gridRow={isMobileBreakpoint ? '-1' : 'auto'}
          >
            <ProjectItemButtons
              item={item}
              onAddClick={handleAddItemClick}
              onEditClick={handleEditItemClick}
              onRemoveClick={handleRemoveItemClick}
              product={product}
            />
          </Box>
          <Box
            data-testid="project-item-personalization-count-info"
            gridColumn="2/4"
          >
            <PersonalizationCountInfo item={item} />
          </Box>
        </Box>
        {isAddingColor && product && (
          <ProjectItemAddModal
            draftProjectItem={{
              design: { cid: item.design?.cid },
              product: { colorId: item.product?.colorId },
              fulfillment: { priority: item.fulfillment?.priority },
            }}
            onClose={handleAddItemClose}
          />
        )}
      </Box>
    </ErrorBoundary>
  );
}
